import {
  Button as MuiButton,
  type ButtonProps as MuiButtonProps,
  styled,
} from '@mui/material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import { typeScale } from '@/shared/styles/typography'

type Tone = 'primary' | 'secondary' | 'danger' | 'ghost'
type Size = 'sm' | 'md'

interface Props extends Omit<MuiButtonProps, 'color' | 'size'> {
  tone?: Tone
  size?: Size
}

const Styled = styled(MuiButton, {
  shouldForwardProp: (p) => p !== 'tone' && p !== 'sz',
})<{ tone: Tone; sz: Size }>(({ theme, tone, sz }) => {
  const tones: Record<Tone, { bg: string; fg: string; hover: string }> = {
    primary: {
      bg: theme.palette.primary.main,
      fg: theme.palette.primary.contrastText,
      hover: theme.palette.primary.dark,
    },
    secondary: {
      bg: colorTokens.bg.muted,
      fg: theme.palette.text.primary,
      hover: colorTokens.bg.emphasis,
    },
    danger: {
      bg: colorTokens.state.dangerBg,
      fg: colorTokens.state.dangerFg,
      hover: colorTokens.state.dangerBg, // 28 spec で danger hover 色未定義、brightness filter で対応
    },
    ghost: {
      bg: 'transparent',
      fg: theme.palette.text.secondary,
      hover: colorTokens.bg.muted,
    },
  }
  const heights: Record<Size, number> = { sm: 28, md: 32 }
  const t = tones[tone]
  // 28 spec で hover 色未定義の tone (danger) は brightness で陰影を付ける。
  // hover 色が bg と同値の時のみ filter を適用 (primary/secondary/ghost には不要)。
  const hoverNeedsFilter = t.hover === t.bg
  return {
    height: heights[sz],
    padding: '0 14px',
    background: t.bg,
    color: t.fg,
    borderRadius: RADIUS.full,
    fontSize: typeScale.label.fontSize,
    fontWeight: typeScale.label.fontWeight,
    '&:hover': {
      background: t.hover,
      ...(hoverNeedsFilter ? { filter: 'brightness(0.96)' } : {}),
    },
    '&:focus-visible': { boxShadow: colorTokens.focus.ringShadow },
    '&:disabled': {
      background: colorTokens.bg.muted,
      color: colorTokens.fg.faint,
    },
  }
})

export const Button = ({ tone = 'primary', size = 'md', ...rest }: Props) => (
  <Styled tone={tone} sz={size} {...rest} />
)
